<template>
  <div class="content">
    <!-- Header: Título + Ações da Página -->
    <div class="header d-flex justify-content-between align-items-center p-2">
      <div class="title">
        <h3 class="h3 mb-n2 bold">Declarações</h3>
        <span class="subtitle-2 grey--text text--darken-1">
          Gerenciamento de declarações e anexos
        </span>
      </div>
      <div class="actions d-flex flex-row align-center">
        <v-btn
          small
          depressed
          style="background-color: #1c1c44; color: white;"
          @click="doLoad"
        >
          <v-icon left dark>mdi-refresh</v-icon>
          <span style="font-size: 12px">Atualizar</span>
        </v-btn>
      </div>
    </div>

    <!-- Tabela 1: Declarações -->
    <MasterDetail
      ref="masterDetail"
      :actionBarButtons="actionBarButtons"
      :canDelete="false"
      :canEdit="false"
      :cols="cols"
      class="master-detail"
      :customResource="customResource"
      disableContextMenu
      disablePagination
      :hasExportCSV="false"
      :hasFilter="false"
      :hasRefresh="false"
      :hasNewButton="false"
      :hasYearFilter="true"
      height="338px"
    />

    <!-- Tabela 2: Anexos -->
    <div v-if="dados.length > 0" class="mt-4">
      <div class="d-flex align-center justify-space-between mb-2 px-2">
        <h4 class="h4 bold">Anexos</h4>
        <v-btn
          small
          depressed
          style="background-color: #1c1c44; color: white;"
          @click="openAnexoForm"
        >
          <v-icon left small>mdi-plus-box-outline</v-icon>
          Adicionar Anexo
        </v-btn>
      </div>
      <v-data-table
        v-if="anexos.length > 0"
        :headers="anexosHeaders"
        :items="anexos"
        hide-default-footer
        disable-pagination
        class="elevation-1"
      >
        <template v-slot:item.actions="{ item }">
          <v-btn icon small :href="item.url" target="_blank" title="Download">
            <v-icon small>mdi-download</v-icon>
          </v-btn>
          <v-btn
            icon
            small
            @click="confirmDeleteAnexo(item)"
            title="Excluir anexo"
          >
            <v-icon small color="red">mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
      <div v-else class="text-center grey--text pa-4">
        Nenhum anexo cadastrado
      </div>
    </div>

    <!-- Modal: Adicionar Anexo -->
    <FormModal
      v-model="isVisibleAnexoForm"
      maxWidth="600"
      :cols="colsAnexoForm"
      title="Adicionar Anexo"
      titleIcon="mdi-paperclip"
      :value.sync="anexoForm"
      @cancel="handleCancelAnexo"
      @save="handleSaveAnexo"
    />

    <!-- Modal: Editar Declaração -->
    <FormModal
      v-model="isVisibleDeclaracaoForm"
      maxWidth="800"
      :cols="colsForm"
      :title="formModalConfig.title"
      :titleIcon="formModalConfig.titleIcon"
      :value.sync="declaracaoValue"
      @cancel="handleCancelDeclaracao"
      @save="handleSaveDeclaracao"
    />
  </div>
</template>

<script>
/**
 * @typedef Declaracao
 * @property {string | null} ano_base
 * @property {boolean} relatorio_demonstrativo
 * @property {boolean} relatorio_simplificado
 * @property {boolean} relatorio_fiscal
 * @property {boolean} certificacao_qualidade
 * @property {boolean} programa_participacao
 * @property {boolean} info_fiscais_fat
 */

import FormModal from "@/components/form-modal.vue";
import MasterDetail from "@/components/master-detail.vue";
import yearsOptions from "@/helpers/yearsOptions.js";
import { isNaN } from "lodash";
import { mapGetters } from "vuex";

/**
 * @function createEmptyFormData
 * @returns {Declaracao}
 */
const createEmptyFormData = () => ({
  ano_base: null,
  certificacao_qualidade: false,
  info_fiscais_fat: false,
  programa_participacao: false,
  relatorio_demonstrativo: false,
  relatorio_fiscal: false,
  relatorio_simplificado: false
});

export default {
  name: "DeclaracaoComponent",
  components: {
    FormModal,
    MasterDetail
  },
  data() {
    return {
      anexos: [],
      anexoForm: { nomeArquivo: "", descricao: "", arquivo: [] },
      dados: [],
      declaracaoValue: createEmptyFormData(),
      isEdit: false,
      isVisibleAnexoForm: false,
      isVisibleDeclaracaoForm: false,
      relatorioSimplificado: false
    };
  },
  computed: {
    ...mapGetters(["clientId", "anoBase"]),
    actionBarButtons() {
      const options = [
        {
          icon: "mdi-plus-box-outline",
          text: "Adicionar",
          show: this.dados.length === 0,
          action: () => {
            this.isEdit = false;
            this.openDeclaracaoForm();
          }
        },
        {
          icon: "mdi-square-edit-outline",
          text: "Editar",
          show: this.dados.length > 0,
          action: () => {
            const item = this.dados[0];
            this.isEdit = true;
            this.declaracaoValue = { ...item };
            this.isVisibleDeclaracaoForm = true;
          }
        },
        {
          icon: "mdi-trash-can-outline",
          text: "Excluir",
          show: this.dados.length > 0,
          action: () => {
            const item = this.dados[0];
            this.showConfirm(
              `Confirmar deleção`,
              `Deseja realmente remover as declarações e anexos cadastrados para o ano de ${item.ano_base}?`,
              () => this.doDelete(item.id)
            );
          }
        }
      ];
      return options.filter(item => item.show !== false);
    },
    colsAnexoForm() {
      return [
        {
          key: "nomeArquivo",
          name: "Nome do arquivo",
          type: this.$fieldTypes.TEXT,
          rules: [{ rule: "required" }],
          colSize: 12
        },
        {
          key: "descricao",
          name: "Descrição",
          type: this.$fieldTypes.TEXTAREA,
          rules: [{ rule: "required" }],
          colSize: 12
        },
        {
          key: "arquivo",
          name: "Arquivo",
          type: this.$fieldTypes.FILE2,
          rules: [{ rule: "required" }],
          colSize: 12
        }
      ];
    },
    anexosHeaders() {
      return [
        { text: "Nome", value: "nomeArquivo" },
        { text: "Descrição", value: "descricao" },
        { text: "Ações", value: "actions", sortable: false, align: "end" }
      ];
    },
    cols() {
      return [
        {
          key: "relatorio",
          name: "Declarações",
          sortable: false
        },
        {
          key: "valor",
          name: "Resposta",
          sortable: false
        }
      ];
    },
    colsForm() {
      const values = [
        { id: false, value: "Não" },
        { id: true, value: "Sim" }
      ];
      return [
        {
          key: "ano_base",
          name: "Ano-base",
          hideInTable: true,
          type: this.$fieldTypes.SELECT,
          rel: { to: yearsOptions.slice(1), key: "value", name: "text" },
          colSize: 3,
          rules: [{ rule: "required" }],
          editable: !this.isEdit
        },
        {
          key: "relatorio_demonstrativo",
          name:
            "A empresa submete ao Ministério da Ciência, Tecnologia e Inovações (MCTI) o Relatório Demonstrativo?",
          rel: { toEdit: values, key: "id", name: "value" },
          type: this.$fieldTypes.SWITCH,
          colSize: 12,
          rules: [{ rule: "required" }]
        },
        {
          key: "relatorio_simplificado",
          name:
            "A empresa optou pelo Relatório Simplificado para este ano-base?",
          rel: { toEdit: values, key: "id", name: "value" },
          type: this.$fieldTypes.SWITCH,
          editable: !this.relatorioSimplificado,
          colSize: 12,
          rules: [{ rule: "required" }]
        },
        {
          key: "relatorio_fiscal",
          name:
            "A empresa declara sua regularidade fiscal, em relação às Certidões Negativas (ou positiva com efeitos de negativa) da SRFB, bem como a sua regularidade com FGTS e CADIN?",
          rel: { toEdit: values, key: "id", name: "value" },
          type: this.$fieldTypes.SWITCH,
          colSize: 12,
          rules: [{ rule: "required" }]
        },
        {
          key: "certificacao_qualidade",
          name:
            "A empresa declara ter implantado a Certificação de Qualidade baseada na norma NBR ISO 9001, ou equivalente?",
          rel: { toEdit: values, key: "id", name: "value" },
          type: this.$fieldTypes.SWITCH,
          colSize: 12,
          rules: [{ rule: "required" }]
        },
        {
          key: "programa_participacao",
          name:
            "A empresa declara ter implantado o Programa de Participação dos Trabalhadores ou Resultados?",
          rel: { toEdit: values, key: "id", name: "value" },
          type: this.$fieldTypes.SWITCH,
          colSize: 12,
          rules: [{ rule: "required" }]
        },
        {
          key: "info_fiscais_fat",
          name:
            "A empresa declara que as informações fiscais e de faturamento apresentadas neste Relatório Demonstrativo correspondem àquelas fornecidas à Receita Federal?",
          rel: { toEdit: values, key: "id", name: "value" },
          type: this.$fieldTypes.SWITCH,
          colSize: 12,
          rules: [{ rule: "required" }]
        }
      ];
    },
    customResource() {
      const resource = this.apiResource(this.resourceUrl);
      const that = this;
      const relatorios = {
        relatorio_demonstrativo:
          "A empresa submete ao Ministério da Ciência, Tecnologia e Inovações (MCTI) o Relatório Demonstrativo?",
        relatorio_simplificado:
          "A empresa optou pelo Relatório Simplificado para este ano-base?",
        relatorio_fiscal:
          "A empresa declara sua regularidade fiscal, em relação às Certidões Negativas (ou positiva com efeitos de negativa) da SRFB, bem como a sua regularidade com FGTS e CADIN?",
        certificacao_qualidade:
          "A empresa declara ter implantado a Certificação de Qualidade baseada na norma NBR ISO 9001, ou equivalente?",
        programa_participacao:
          "A empresa declara ter implantado o Programa de Participação dos Trabalhadores ou Resultados?",
        info_fiscais_fat:
          "A empresa declara que as informações fiscais e de faturamento apresentadas neste Relatório Demonstrativo correspondem àquelas fornecidas à Receita Federal?"
      };

      return {
        ...resource,
        get(args) {
          return resource.get(args).then(response => {
            that.dados = response.data;
            that.relatorioSimplificado = !!response.relatorioSimplificado;

            // Só força o valor quando o relatório do cliente é "simplificado"
            // (cenário de trava). Caso contrário, mantém o valor gravado no banco.
            if (that.dados.length > 0 && that.relatorioSimplificado) {
              that.dados[0].relatorio_simplificado = true;
            }

            let result = [];
            that.anexos = [];

            if (response.data.length > 0) {
              const item = response.data[0];

              result = Object.keys(item)
                .filter(key => Object.keys(relatorios).includes(key))
                .map(key => {
                  const targetValue =
                    key === "relatorio_simplificado"
                      ? that.relatorioSimplificado || !!item[key]
                      : item[key];

                  return {
                    id: item.id,
                    relatorio: relatorios[key],
                    valor: targetValue ? "Sim" : "Não"
                  };
                });

              if (Array.isArray(item.anexos) && item.anexos.length > 0) {
                that.anexos = item.anexos.map(anexo => ({
                  id: anexo.id,
                  declaracaoId: item.id,
                  nomeArquivo: anexo.nomeArquivo,
                  descricao: anexo.originalInfo || "",
                  url: anexo.url
                }));
              }
            }

            return result;
          });
        }
      };
    },
    formModalConfig() {
      return {
        title: `${this.isEdit ? "Edição" : "Cadastro"} de declarações`,
        titleIcon: this.isEdit
          ? "mdi-square-edit-outline"
          : "mdi-plus-box-outline"
      };
    },
    resourceUrl() {
      return `/v1/rda/${this.clientId}/declaracoes`;
    }
  },
  methods: {
    doLoad() {
      this.$refs.masterDetail.doLoad();
    },
    confirmDeleteAnexo(anexo) {
      this.showConfirm(
        "Confirmar exclusão",
        `Deseja realmente remover o anexo "${anexo.nomeArquivo}"?`,
        () => this.deleteAnexo(anexo)
      );
    },
    async deleteAnexo(anexo) {
      try {
        const url = `${this.resourceUrl}/${anexo.declaracaoId}/anexo/${anexo.id}`;
        await this.api().delete(url);
        this.$notify({
          group: "geral",
          type: "success",
          title: "Sucesso",
          text: "Anexo removido com sucesso!",
          duration: 5000
        });
        this.doLoad();
      } catch (error) {
        this.errorHandler(error);
      }
    },
    async saveDeclaracao(dados) {
      const loadingProcess = "post-declaracao-rda";
      this.$emit("addLoadingProcess", loadingProcess);

      try {
        const empresaId = this.clientId;
        const {
          id,
          relatorio_demonstrativo,
          relatorio_simplificado,
          relatorio_fiscal,
          certificacao_qualidade,
          programa_participacao,
          info_fiscais_fat,
          ano_base
        } = dados;
        const formData = new FormData();

        const parseBoolean = bool => (bool ? 1 : 0);

        if (id) {
          formData.append("id", id);
        }

        if (this.relatorioSimplificado) {
          formData.append("relatorio_simplificado", 1);
        } else {
          formData.append(
            "relatorio_simplificado",
            parseBoolean(relatorio_simplificado)
          );
        }

        formData.append(
          "relatorio_demonstrativo",
          parseBoolean(relatorio_demonstrativo)
        );
        formData.append("relatorio_fiscal", parseBoolean(relatorio_fiscal));
        formData.append(
          "certificacao_qualidade",
          parseBoolean(certificacao_qualidade)
        );
        formData.append(
          "programa_participacao",
          parseBoolean(programa_participacao)
        );
        formData.append("info_fiscais_fat", parseBoolean(info_fiscais_fat));
        formData.append("ano_base", ano_base);
        formData.append("empresa_id", parseBoolean(empresaId));

        const url = id ? `${this.resourceUrl}/${id}` : this.resourceUrl;
        await this.api().post(url, formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        });

        this.$notify({
          group: "geral",
          type: "success",
          title: "Sucesso",
          text: `Declaração ${id ? "atualizada" : "criada"}.`,
          duration: 5000
        });

        this.isVisibleDeclaracaoForm = false;
        this.$refs.masterDetail.doLoad();
      } catch (error) {
        this.errorHandler(error);
      } finally {
        this.$emit("removeLoadingProcess", loadingProcess);
      }
    },
    doDelete(id) {
      const resource = this.apiResource(this.resourceUrl);
      return resource
        .delete(id)
        .then(result => {
          this.$notify({
            group: "geral",
            type: "success",
            title: "Sucesso",
            text: "Declaração removida com sucesso!",
            duration: 5000
          });
          this.$refs.masterDetail.doLoad();
          return result;
        })
        .catch(error => {
          this.errorHandler(error);
        });
    },
    handleSaveDeclaracao() {
      this.saveDeclaracao(this.declaracaoValue);
    },
    handleCancelDeclaracao() {
      this.declaracaoValue = createEmptyFormData();
      this.isVisibleDeclaracaoForm = false;
    },
    openDeclaracaoForm() {
      this.declaracaoValue = createEmptyFormData();
      const anoBase = parseInt(this.anoBase, 10);

      if (!isNaN(anoBase)) {
        this.declaracaoValue.ano_base = anoBase;
      }

      this.declaracaoValue.relatorio_simplificado = !!this.relatorioSimplificado;

      this.isVisibleDeclaracaoForm = true;
    },
    openAnexoForm() {
      this.anexoForm = { nomeArquivo: "", descricao: "", arquivo: [] };
      this.isVisibleAnexoForm = true;
    },
    handleCancelAnexo() {
      this.anexoForm = { nomeArquivo: "", descricao: "", arquivo: [] };
      this.isVisibleAnexoForm = false;
    },
    async handleSaveAnexo() {
      try {
        const declaracao = this.dados[0];

        if (!declaracao) {
          this.notify({
            error: "Nenhuma declaração encontrada."
          });

          return;
        }

        const file = this.anexoForm.arquivo[0];

        if (!file) {
          this.notify({
            error: "Selecione um arquivo."
          });

          return;
        }

        const formData = new FormData();
        formData.append("anexos[]", file);
        formData.append(
          "nomes_anexos[]",
          this.anexoForm.nomeArquivo || file.name
        );
        formData.append("descricoes_anexos[]", this.anexoForm.descricao || "");

        formData.append("ano_base", declaracao.ano_base);
        formData.append("empresa_id", this.clientId);
        formData.append(
          "relatorio_demonstrativo",
          declaracao.relatorio_demonstrativo ? 1 : 0
        );
        formData.append(
          "relatorio_simplificado",
          declaracao.relatorio_simplificado ? 1 : 0
        );
        formData.append(
          "relatorio_fiscal",
          declaracao.relatorio_fiscal ? 1 : 0
        );
        formData.append(
          "certificacao_qualidade",
          declaracao.certificacao_qualidade ? 1 : 0
        );
        formData.append(
          "programa_participacao",
          declaracao.programa_participacao ? 1 : 0
        );
        formData.append(
          "info_fiscais_fat",
          declaracao.info_fiscais_fat ? 1 : 0
        );

        await this.api().post(
          `${this.resourceUrl}/${declaracao.id}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        this.$notify({
          group: "geral",
          type: "success",
          title: "Sucesso",
          text: "Anexo adicionado com sucesso!",
          duration: 5000
        });

        this.isVisibleAnexoForm = false;
        this.doLoad();
      } catch (error) {
        this.errorHandler(error);
      }
    },
    goHome() {
      this.notify({
        error:
          "Não é possível acessar o módulo de Prestação de Contas sem uma empresa selecionada."
      });

      this.$router.push({
        name: "homepage"
      });
    }
  },
  created() {
    if (!this.clientId) {
      this.goHome();
    }
  },
  watch: {
    clientId(value) {
      if (!value) {
        this.goHome();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.bg-color {
  background-color: #81b4ea !important;
}

.content {
  padding: 8px;
}

.master-detail::v-deep tbody {
  .table-v-tr > td:nth-child(1) {
    padding-left: 1rem;
  }
}
</style>
